/**
 * Page Display Rules - Per-page widget display mode resolution
 *
 * Evaluates page_display_rules from traffic control config to determine
 * widget display mode per URL.
 *
 * Display modes:
 * - 'displayed': Widget in normal (full) mode
 * - 'hidden': Widget completely hidden
 * - 'minimized': Widget in hidden_zone_click (button only) mode
 */

import { logger } from '../core/logger';
import { matchesUrlPattern } from '../web/urlUtils';

export type PageDisplayMode = 'displayed' | 'hidden' | 'minimized';

export interface PageDisplayRule {
    pattern: string;
    display: PageDisplayMode;
}

/**
 * Resolve which page display rule matches the current URL.
 *
 * Rules are evaluated in order; first match wins.
 *
 * @param rules - Array of page display rules from traffic control config
 * @param url - Current page URL to match against
 * @returns The matched rule or null if no rule matches
 */
export function resolvePageDisplayRule(
    rules: PageDisplayRule[] | undefined,
    url: string
): PageDisplayRule | null {
    if (!rules || rules.length === 0) {
        return null;
    }

    for (const rule of rules) {
        if (matchesUrlPattern(url, rule.pattern)) {
            logger.debug(`Page display rule matched: "${rule.pattern}" → ${rule.display}`);
            return rule;
        }
    }

    return null;
}
